import { useState, type FormEvent } from 'react';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type { Group, GroupBody, TeamMember } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input, Label } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { teamListQuery } from '@/features/team/team-queries';
import { errorMessage, groupsListOptions } from '@/features/groups/groups-queries';

/**
 * Grupos de atendentes (visibilidade de mesas): lista com membros em chips,
 * criar/editar em dialog (nome, ativo, membros por checkboxes) e exclusão.
 *
 * Spec 061: o grupo espelhado do TiFlux leva o selo "TiFlux". Nele, o nome e a
 * participação de quem vem de lá são da sincronização (desabilitados aqui, que
 * a rodada seguinte desfaria); quem foi criado no ERP se inclui aqui e fica.
 */
export function GroupsPage() {
  const queryClient = useQueryClient();
  // a equipe deste tenant, inativos inclusive (membro inativo segue com nome);
  // quem está fora dela aparece como "Usuário #id" (pedido da PoolTrack, 05/10)
  const [groupsQ, usersQ] = useQueries({
    queries: [groupsListOptions(), teamListQuery({ includeInactive: true })],
  });
  const [dialog, setDialog] = useState<{ group: Group | null } | null>(null);

  const remove = useMutation({
    mutationFn: async (groupId: number) => {
      const res = await api<void>(`/groups/${groupId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['erp', 'groups'] }),
  });

  const usersById = new Map((usersQ.data?.items ?? []).map((u) => [u.id, u]));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">Grupos de atendentes</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Mesas com grupos vinculados ficam visíveis apenas aos membros (e a administradores).
          </p>
        </div>
        <Button size="sm" onClick={() => setDialog({ group: null })}>
          <Plus className="size-4" strokeWidth={2.5} />
          Novo grupo
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
        {groupsQ.isPending ? (
          <div className="space-y-2 p-4" aria-busy>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : groupsQ.isError ? (
          <EmptyState message={`Erro ao carregar grupos: ${errorMessage(groupsQ.error)}`} />
        ) : (groupsQ.data ?? []).length === 0 ? (
          <EmptyState message="Nenhum grupo cadastrado" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-9 px-4 font-semibold">Nome</th>
                  <th className="h-9 px-4 font-semibold">Membros</th>
                  <th className="h-9 w-24 px-4 font-semibold">Situação</th>
                  <th className="h-9 w-20 px-4">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(groupsQ.data ?? []).map((g) => (
                  <tr
                    key={g.id}
                    className="border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className="max-w-52 truncate px-4 py-2.5 align-top font-medium">
                      {g.name}
                      {g.tifluxManagedUserIds !== null ? (
                        <Badge variant="outline" className="ml-1.5 align-middle text-[11px] font-normal">
                          TiFlux
                        </Badge>
                      ) : null}
                    </td>
                    <td className="px-4 py-2">
                      {g.memberIds.length === 0 ? (
                        <span className="text-muted-foreground">Sem membros</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {g.memberIds.map((id) => (
                            <Badge key={id} variant="outline">
                              {usersById.get(id)?.name ?? `Usuário #${id}`}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 align-top whitespace-nowrap">
                      <span
                        className={cn(
                          'text-[13px] font-medium',
                          g.active ? 'text-[#52C41A]' : 'text-muted-foreground'
                        )}
                      >
                        {g.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-4 py-2 align-top">
                      <div className="flex justify-end gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          className="text-muted-foreground"
                          title="Editar grupo"
                          onClick={() => setDialog({ group: g })}
                        >
                          <Pencil className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          className="text-muted-foreground hover:text-destructive"
                          title="Excluir grupo"
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm(`Excluir o grupo "${g.name}"?`)) remove.mutate(g.id);
                          }}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {remove.error ? (
          <p className="px-4 py-3 text-sm text-destructive">{errorMessage(remove.error)}</p>
        ) : null}
      </div>

      {dialog ? (
        <GroupDialog
          group={dialog.group}
          users={usersQ.data?.items ?? []}
          teamLoaded={usersQ.isSuccess}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}

function GroupDialog({
  group,
  users,
  teamLoaded,
  onClose,
}: {
  group: Group | null;
  users: TeamMember[];
  /** sem a equipe carregada, todo membro pareceria "fora da equipe" */
  teamLoaded: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(group?.name ?? '');
  const [active, setActive] = useState(group?.active ?? true);
  const [memberIds, setMemberIds] = useState<Set<number>>(new Set(group?.memberIds ?? []));
  // spec 061: grupo do TiFlux ⇒ quem vem de lá (a participação é a de lá)
  const fromTiflux = (group?.tifluxManagedUserIds ?? null) !== null;
  const managed = new Set(group?.tifluxManagedUserIds ?? []);

  const save = useMutation({
    mutationFn: async (body: GroupBody) => {
      const res = group
        ? await api<Group>(`/groups/${group.id}`, { method: 'PUT', body })
        : await api<Group>('/groups', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'groups'] });
      // Vínculos mesa↔grupo exibem nomes/membros — mantém as seções em dia.
      void queryClient.invalidateQueries({ queryKey: ['erp', 'desks', 'groups'] });
      onClose();
    },
  });

  const toggleMember = (userId: number, checked: boolean) => {
    setMemberIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(userId);
      else next.delete(userId);
      return next;
    });
  };

  // Só a equipe deste tenant entra em grupo (a lista já é ela, e a API recusa
  // acrescentar quem está fora); inativos aparecem apenas se já membros.
  const selectable: Array<{ id: number; name: string; email: string }> = users.filter(
    (u) => u.active || memberIds.has(u.id)
  );
  // Membro antigo FORA da equipe (login de portal, outra empresa) segue na
  // lista, marcado, para poder ser tirado — a API não o recusa por já estar lá,
  // só não deixa acrescentar ninguém de fora.
  const known = new Set(users.map((u) => u.id));
  for (const id of teamLoaded ? memberIds : []) {
    if (!known.has(id)) selectable.push({ id, name: `Usuário #${id}`, email: 'fora da equipe' });
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    save.mutate({ name: name.trim(), active, userIds: [...memberIds] });
  };

  return (
    <Dialog open onClose={onClose} title={group ? 'Editar grupo' : 'Novo grupo'}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="group-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="group-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus={!fromTiflux}
            disabled={fromTiflux}
            aria-describedby={fromTiflux ? 'group-name-tiflux' : undefined}
          />
          {fromTiflux ? (
            <p id="group-name-tiflux" className="text-xs text-muted-foreground">
              O nome vem do grupo técnico do TiFlux.
            </p>
          ) : null}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
          <Switch checked={active} onCheckedChange={setActive} />
          Grupo ativo
        </label>
        <div className="space-y-1.5">
          <Label>Membros</Label>
          {fromTiflux ? (
            <p className="text-xs text-muted-foreground" data-tiflux-note>
              Quem tem o selo TiFlux entra e sai deste grupo pelo grupo técnico de lá. Quem foi criado no ERP você
              inclui aqui, e a sincronização o mantém.
            </p>
          ) : null}
          {selectable.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum atendente disponível.</p>
          ) : (
            <ul className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
              {selectable.map((u) => {
                // spec 061: num grupo do TiFlux, quem vem de lá não se marca aqui
                const locked = fromTiflux && managed.has(u.id);
                return (
                  <li key={u.id}>
                    <label
                      className={cn(
                        'flex items-center gap-2.5 rounded-md border border-divider px-3 py-2 text-sm transition-colors',
                        locked ? 'cursor-default bg-muted/40' : 'cursor-pointer hover:border-border'
                      )}
                      data-tiflux-locked={locked ? '' : undefined}
                    >
                      <Checkbox
                        checked={memberIds.has(u.id)}
                        disabled={locked}
                        onCheckedChange={(checked) => toggleMember(u.id, checked === true)}
                      />
                      <span className="min-w-0 truncate font-medium">{u.name}</span>
                      {locked ? (
                        <Badge variant="outline" className="shrink-0 text-[11px] font-normal">
                          TiFlux
                        </Badge>
                      ) : null}
                      <span className="ml-auto shrink-0 truncate text-xs text-muted-foreground">
                        {u.email}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !name.trim()}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
